/**
 * Helpers for rendering telephony-call activities (`type === 'call'`).
 *
 * Telephony activities arrive with:
 *   - `description` — markdown-flavoured text with `## Samenvatting`,
 *     `## Actiepunten` and `## Transcript` sections.
 *   - `metadata` — structured payload (direction, duration, recording url, ...).
 *
 * The parser is defensive: if section headers are missing/different
 * we fall back to dumping everything into `summary`.
 */

const HEADER_REGEX = /^\s*(?:#{1,3}|\*\*)\s*([^\n*#]+?)(?:\s*\*\*)?\s*$/;

const SECTION_KEYS = {
  samenvatting: 'summary',
  summary: 'summary',
  overzicht: 'summary',
  notes: 'summary',

  actiepunten: 'actionItems',
  'action items': 'actionItems',
  'next steps': 'actionItems',
  todo: 'actionItems',

  transcript: 'transcript',
  transcriptie: 'transcript',
};

const normalizeHeader = (label) =>
  String(label || '')
    .toLowerCase()
    .replace(/[:?!.]+$/g, '')
    .trim();

/**
 * Parse a markdown-flavoured call description into structured sections.
 * Returns `{ summary, actionItems, transcript, raw, hasStructure }`.
 */
export const parseCallDescription = (description) => {
  const result = {
    summary: '',
    actionItems: '',
    transcript: '',
    raw: typeof description === 'string' ? description : '',
    hasStructure: false,
  };

  if (!description || typeof description !== 'string') {
    return result;
  }

  const lines = description.replace(/\r\n/g, '\n').split('\n');
  let currentKey = null;
  const buckets = { summary: [], actionItems: [], transcript: [] };

  for (const line of lines) {
    const headerMatch = line.match(HEADER_REGEX);
    const headerLabel = headerMatch ? normalizeHeader(headerMatch[1]) : null;
    const headerKey = headerLabel ? SECTION_KEYS[headerLabel] : null;

    if (headerKey) {
      currentKey = headerKey;
      result.hasStructure = true;
      continue;
    }

    if (currentKey) {
      buckets[currentKey].push(line);
    } else {
      buckets.summary.push(line);
    }
  }

  result.summary = (buckets.summary.join('\n') || '').trim();
  result.actionItems = (buckets.actionItems.join('\n') || '').trim();
  result.transcript = (buckets.transcript.join('\n') || '').trim();

  if (!result.hasStructure && !result.summary) {
    result.summary = result.raw.trim();
  }

  return result;
};

/**
 * Format a duration in seconds as "Nm Ms" (e.g. "7m 32s").
 */
export const formatDuration = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  const remaining = total % 60;
  if (minutes === 0) return `${remaining}s`;
  return `${minutes}m ${remaining.toString().padStart(2, '0')}s`;
};

/**
 * Determine whether an activity originated from the telephony integration.
 * Accepts either a flag (`is_auto_generated`) or an `external_id`.
 */
export const isTelephonyActivity = (activity) => {
  if (!activity || activity.type !== 'call') return false;
  return Boolean(activity.is_auto_generated) || Boolean(activity.external_id);
};

/**
 * Convert a list of action-items text (one per line, may have `-`/`*` bullets)
 * into a clean array of strings. Lines without bullets are preserved as-is.
 */
export const splitActionItems = (text) => {
  if (!text) return [];
  return text
    .split('\n')
    .map((line) => line.replace(/^\s*[-*•]\s*/, '').trim())
    .filter(Boolean);
};
